import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Users, Truck, Wallet, HandCoins, Package, Landmark, Coins, TrendingUp, TrendingDown, CalendarClock, LineChart, FileBarChart, Settings, LogOut, Search, Bell, Menu, Calculator, FileText, UserCircle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/satislar", label: "Satışlar", icon: ShoppingCart, testid: "nav-sales" },
  { to: "/teklifler", label: "Teklifler", icon: FileText, testid: "nav-quotes" },
  { to: "/musteriler", label: "Müşteriler", icon: Users, testid: "nav-customers" },
  { to: "/tedarikciler", label: "Tedarikçiler", icon: Truck, testid: "nav-suppliers" },
  { to: "/tahsilatlar", label: "Tahsilatlar", icon: HandCoins, testid: "nav-payments" },
  { to: "/urunler", label: "Ürünler / Stok", icon: Package, testid: "nav-products" },
  { to: "/hesaplama", label: "Ağırlık Hesaplama", icon: Calculator, testid: "nav-calc" },
  { to: "/bankalar", label: "Bankalar", icon: Landmark, testid: "nav-banks" },
  { to: "/kasalar", label: "Kasalar", icon: Coins, testid: "nav-cash" },
  { to: "/gelirler", label: "Gelirler", icon: TrendingUp, testid: "nav-income" },
  { to: "/giderler", label: "Giderler", icon: TrendingDown, testid: "nav-expenses" },
  { to: "/yaklasan", label: "Yaklaşan İşlemler", icon: CalendarClock, testid: "nav-upcoming" },
  { to: "/nakit-akisi", label: "Nakit Akışı", icon: LineChart, testid: "nav-cashflow" },
  { to: "/raporlar", label: "Raporlar", icon: FileBarChart, testid: "nav-reports" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, testid: "nav-settings", admin: true },
  { to: "/yonetim", label: "Yönetim", icon: ShieldCheck, testid: "nav-admin", admin: true },
  { to: "/ayarlar", label: "Hesabım", icon: UserCircle, testid: "nav-account", staff: true },
];

export default function Layout({ children }) {
  const { user, isAdmin, logout } = useAuth();
  const [q, setQ] = React.useState("");
  const [res, setRes] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [alertCount, setAlertCount] = React.useState(0);
  const nav_go = useNavigate();
  const navItems = nav.filter((n) => (n.admin ? isAdmin : n.staff ? !isAdmin : true));

  React.useEffect(() => {
    Promise.all([
      api.get("/upcoming/collections", { params: { days: 7 } }),
      api.get("/upcoming/payments", { params: { days: 7 } }),
    ]).then(([c, p]) => setAlertCount(c.data.length + p.data.length)).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setRes(data); setOpen(true);
      } catch (err) {
        console.error("Arama hatası:", err);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <img src="https://customer-assets-v7afamib.emergentagent.net/job_demir-finans/artifacts/o8x43ruo_IMG_20220611_144831_918.png"
            alt="Badem Ticaret" className="h-10 w-auto object-contain invert" />
          <div>
            <div className="font-bold text-base leading-tight">Badem Ticaret</div>
            <div className="text-xs text-muted-foreground">Cari Yönetim</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-0.5">
        {navItems.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            onClick={() => setMobileOpen(false)}>
            <n.icon size={17} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <button onClick={logout} data-testid="logout-btn"
          className="nav-item w-full text-left hover:!bg-destructive/10 hover:!text-destructive">
          <LogOut size={17} /> <span>Çıkış Yap</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card sticky top-0 h-screen">
        {SidebarInner}
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)}>
          <aside className="w-64 h-full bg-card border-r border-border" onClick={(e) => e.stopPropagation()}>
            {SidebarInner}
          </aside>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-card/95 backdrop-blur-md flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <div className="flex-1 max-w-md relative">
            <Popover open={open && !!res} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Müşteri, ürün ara..."
                    className="pl-9 h-10 bg-background" data-testid="global-search-input" />
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0 bg-popover border-border" align="start">
                {res && (
                  <div className="max-h-96 overflow-y-auto scrollbar-thin">
                    <div className="p-2">
                      <div className="text-xs px-2 py-1 text-muted-foreground uppercase font-semibold">Müşteriler</div>
                      {res.customers?.length ? res.customers.map((c) => (
                        <button key={c.id} onClick={() => { nav_go(`/musteriler/${c.id}`); setOpen(false); setQ(""); }}
                          className="w-full text-left px-2 py-2 rounded hover:bg-accent text-sm">
                          {c.name}
                        </button>
                      )) : <div className="px-2 py-1 text-sm text-muted-foreground">-</div>}
                    </div>
                    <div className="p-2 border-t border-border">
                      <div className="text-xs px-2 py-1 text-muted-foreground uppercase font-semibold">Ürünler</div>
                      {res.products?.length ? res.products.map((p) => (
                        <div key={p.id} className="px-2 py-2 text-sm">{p.name} · {p.size}</div>
                      )) : <div className="px-2 py-1 text-sm text-muted-foreground">-</div>}
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <button className="relative p-2 rounded-md hover:bg-accent" data-testid="notifications-btn" onClick={() => nav_go("/yaklasan")} title="Vade uyarıları">
            <Bell size={18} />
            {alertCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">{alertCount}</span>
            )}
          </button>
          <div className="flex items-center gap-2 pl-3 border-l border-border">
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
              {user?.name?.[0] ?? "U"}
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.role === "admin" ? "Yönetici" : "Personel"}</div>
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
